import React, { useMemo, Dispatch, SetStateAction } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { calculateSharePartialDisposalTax } from '../../../../shared/src/calculation/entityDisposalTax.ts';
import type { FactFind, FactFindAsset } from '../../../../shared/src/calculation/factFindTypes.ts';
import { DisposalCgtEstimatePanel } from './DisposalCgtEstimatePanel.tsx';

// DisposalCgtEstimatePanel.tsx is outside this migration slice's file list (typed by a concurrent
// agent) — its untyped destructured-props signature currently infers each prop's type from its
// default value alone (e.g. `entityId = null` → `null`), which rejects the real string/number
// values this file passes. Widen the reference locally rather than editing that file.
const CgtEstimatePanel = DisposalCgtEstimatePanel as unknown as React.ComponentType<Record<string, unknown>>;
import {
    ENTITY_STRUCTURE_FUNDING_DISCLAIMER,
    computeRadFundingTotals,
    getEntityLiquidGroupsForRad,
    getPersonalLiquidAssetsForRad,
    sumEntityRadFunding,
} from '../../utils/entityFundingUtils.ts';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

// Built by entityFundingUtils.ts's buildRadFundingAssetRow (internal, not exported) — reconstructed
// here via ReturnType so this file gets the real shape instead of `any`.
type RadFundingAsset = ReturnType<typeof getPersonalLiquidAssetsForRad>[number];
type EntityLiquidGroup = ReturnType<typeof getEntityLiquidGroupsForRad>[number];
type RadFunding = Record<string, number>;
type CgtOverrides = Record<string, unknown>;

interface RadFundingAssetSliderProps {
    asset: RadFundingAsset;
    saleAmount: number;
    onFundingChange: (assetId: string, value: string | number | undefined) => void;
    /** Already claimed of this asset by the Debt Strategy card — see `reservedByAsset`. */
    reserved?: number;
}

function RadFundingAssetSlider({ asset, saleAmount, onFundingChange, reserved = 0 }: RadFundingAssetSliderProps) {
    // What is left of this asset AFTER the debt payoff's claim on it. A dollar can retire a debt or pay
    // a deposit, not both, so the two cards net off here rather than being reconciled afterwards.
    const available = Math.max(0, (parseFloat(String(asset.value)) || 0) - reserved);

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700">
                {asset.description || asset.typeName}
                {asset.typeId === 'shares' && asset.costBase ? (
                    <span className="block text-xs font-normal text-gray-500">
                        Amount to liquidate (CGT deducted from RAD credited)
                    </span>
                ) : null}
            </label>
            <div className="flex items-center gap-2">
                <input
                    type="range"
                    min="0"
                    max={available}
                    value={saleAmount}
                    onChange={(e) => onFundingChange(asset.assetId, e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="w-36">
                    <CurrencyInput
                        value={saleAmount || 0}
                        onChange={(v) => onFundingChange(asset.assetId, v)}
                        placeholder="0"
                        className=""
                    />
                </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">
                <span>Available: {formatCurrency(available)}</span>
                {reserved > 0 && (
                    <span className="text-gray-400"> ({formatCurrency(reserved)} allocated to debt repayment)</span>
                )}
            </div>
        </div>
    );
}

interface RadFundingShareCgtPanelProps {
    factFind: DraftFactFind;
    asset: RadFundingAsset;
    saleAmount: number;
    entityId?: string;
    cgtOverrides: CgtOverrides;
    onCgtOverrideChange: (assetId: string, value: number | null) => void;
}

function RadFundingShareCgtPanel({
    factFind,
    asset,
    saleAmount,
    entityId,
    cgtOverrides,
    onCgtOverrideChange,
}: RadFundingShareCgtPanelProps) {
    if (!saleAmount || saleAmount <= 0 || !asset.costBase) return null;

    // DraftFactFind is structurally compatible with FactFind/FactFindAsset
    // (formDraft.ts); the shared calc-engine fn defensively coerces internally regardless
    // (factFindTypes.ts header), so this cast mirrors entityFundingUtils.ts's own.
    const taxSummary = calculateSharePartialDisposalTax(
        factFind as unknown as FactFind,
        asset as unknown as FactFindAsset,
        saleAmount,
        { trustDisposal: 'distribute' },
    );

    return (
        <CgtEstimatePanel
            factFind={factFind}
            taxSummary={taxSummary}
            overrideValue={cgtOverrides?.[asset.assetId]}
            onOverrideChange={(value: number | null) => onCgtOverrideChange(asset.assetId, value)}
            entityId={entityId}
            asset={asset}
            showNetProceedsLine
            saleAmount={saleAmount}
            netProceedsLabel="Net toward RAD (after estimated CGT)"
        />
    );
}

interface RadFundingStrategyProps {
    factFind: DraftFactFind;
    radPaid: number;
    radInput: number;
    radFunding: RadFunding;
    setRadFunding: Dispatch<SetStateAction<RadFunding>>;
    cgtOverrides?: CgtOverrides;
    setCgtOverrides?: Dispatch<SetStateAction<CgtOverrides>>;
    strategyMode: string;
    /**
     * What the Debt Strategy card has already claimed of each asset, keyed the same `type-id` way.
     *
     * Both cards spend from one pool, so each slider's ceiling is what the other has left of that
     * asset. Passed in rather than derived here because only the owner holds both allocations.
     */
    reservedByAsset?: RadFunding;
    /**
     * The "when are these sold?" control, rendered directly under the funding totals.
     *
     * Passed in rather than built here so its state stays with the rest of the modelling state in
     * AgedCareAnalyser. It belongs at this point on the page because it is a consequence of the
     * funding plan, not of the facility's costs — it only exists once the client has decided to sell
     * something, and it sits beside the CGT figures it interacts with. It used to live three cards
     * further up, next to the care start date it merely depends on.
     */
    saleTimingSlot?: React.ReactNode;
}

const RadFundingStrategy = ({
    factFind,
    radPaid,
    radInput,
    radFunding,
    setRadFunding,
    cgtOverrides = {},
    setCgtOverrides,
    strategyMode,
    reservedByAsset = {},
    saleTimingSlot = null,
}: RadFundingStrategyProps) => {
    const personalLiquidAssets = useMemo(
        () => getPersonalLiquidAssetsForRad(factFind),
        [factFind.assets]
    );

    const entityLiquidGroups: EntityLiquidGroup[] = useMemo(
        () => getEntityLiquidGroupsForRad(factFind),
        [factFind.assets, factFind.structures]
    );

    const fundingTotals = useMemo(
        () => computeRadFundingTotals(factFind, radFunding, cgtOverrides),
        [factFind, radFunding, cgtOverrides]
    );

    const entityRadGrossAllocated = useMemo(
        () => sumEntityRadFunding(radFunding, entityLiquidGroups),
        [radFunding, entityLiquidGroups]
    );

    const otherRadPaid = Math.max(0, (parseFloat(String(radPaid)) || 0) - fundingTotals.netTotal);

    const handleFundingChange = (assetId: string, rawValue: string | number | undefined) => {
        const value = parseFloat(String(rawValue)) || 0;
        const newFunding = { ...radFunding, [assetId]: value };
        const projected = computeRadFundingTotals(factFind, newFunding, cgtOverrides);
        if (projected.netTotal + otherRadPaid > radInput) {
            return;
        }

        setRadFunding(newFunding);

        if (value <= 0 && setCgtOverrides) {
            setCgtOverrides((prev) => {
                if (prev[assetId] == null) return prev;
                const next = { ...prev };
                delete next[assetId];
                return next;
            });
        }
    };

    const handleCgtOverrideChange = (assetId: string, value: number | null) => {
        if (!setCgtOverrides) return;
        const newOverrides = { ...cgtOverrides };
        if (value == null) {
            delete newOverrides[assetId];
        } else {
            newOverrides[assetId] = value;
        }
        const projected = computeRadFundingTotals(factFind, radFunding, newOverrides);
        if (projected.netTotal + otherRadPaid > radInput) {
            return;
        }
        setCgtOverrides(newOverrides);
    };

    const remainingToFund = radInput - radPaid;

    const totalCash = (factFind.assets?.bankAccounts || [])
        .filter((a) => !String(a.ownership || '').startsWith('entity:'))
        .reduce((sum, a) => sum + (parseFloat(String(a.value)) || 0), 0);
    const cashAllocated = (factFind.assets?.bankAccounts || [])
        .filter((a) => !String(a.ownership || '').startsWith('entity:'))
        .reduce(
            (sum, a) => sum + (radFunding[`bankAccounts-${a.id}`] || 0),
            0
        );
    const remainingCash = totalCash - cashAllocated;

    const futureCapitalExpenses = (factFind.expenses?.capitalExpenses || []).reduce(
        (sum, exp) => sum + (parseFloat(String(exp.amount)) || 0),
        0
    );
    // Rule of thumb: keep at least 6 months of expenses (or the floor) as an emergency fund. The client can still
    // choose to spend below this — it's a warning, not a block.
    const recommendedEmergencyFund = getRecommendedEmergencyFund(factFind);
    const liquidityShortfall =
        recommendedEmergencyFund + futureCapitalExpenses - remainingCash;

    const hasStructures = (factFind.structures || []).length > 0;
    const hasShareLiquidation = fundingTotals.shareCgtTotal > 0;

    return (
        <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">
                Paying for the room
            </h3>

            {strategyMode === 'manual' && (
                <div>
                    {/* The heading used to read "RAD Funding Strategy (From Existing Assets)", which a
                        client read as an INSTRUCTION to pay a lump sum: "I don't want to pay the RAD!"
                        (adviser-reported, 2026-08-21). Paying nothing up front is a legitimate choice —
                        the room is then paid for daily instead — so the zero state now says so plainly
                        rather than leaving an untouched set of sliders looking like unfinished work. */}
                    <p className="text-sm text-gray-600 mb-4">
                        You can pay for the room as a lump sum, as a daily payment, or as a mix of the
                        two. Move a slider to pay part of it up front from that asset; leave everything
                        at zero to pay for the room daily instead. Where shares are sold, estimated
                        capital gains tax comes out before the money reaches the room deposit.
                    </p>

                    {fundingTotals.netTotal === 0 && otherRadPaid === 0 && (
                        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                            <p className="text-sm text-slate-700">
                                <strong>Nothing paid up front.</strong> The whole room price is being paid
                                as a daily payment. That is a valid strategy — you can leave this as it is.
                            </p>
                        </div>
                    )}

                    {personalLiquidAssets.length > 0 && (
                        <div className="space-y-4">
                            {hasStructures && (
                                <h4 className="text-sm font-semibold text-gray-700">
                                    Your personal assets
                                </h4>
                            )}
                            {personalLiquidAssets.map((asset) => {
                                const saleAmount = radFunding[asset.assetId] || 0;
                                return (
                                    <div key={asset.assetId} className="space-y-2">
                                        <RadFundingAssetSlider
                                            asset={asset}
                                            saleAmount={saleAmount}
                                            onFundingChange={handleFundingChange}
                                            reserved={reservedByAsset[asset.assetId] || 0}
                                        />
                                        {asset.typeId === 'shares' && saleAmount > 0 && (
                                            <RadFundingShareCgtPanel
                                                factFind={factFind}
                                                asset={asset}
                                                saleAmount={saleAmount}
                                                cgtOverrides={cgtOverrides}
                                                onCgtOverrideChange={handleCgtOverrideChange}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {entityLiquidGroups.map((group) => (
                        <div key={group.entityId} className="mt-6 pt-4 border-t space-y-4">
                            <div>
                                <h4 className="text-sm font-semibold text-gray-800">
                                    {group.entityName}
                                </h4>
                                <p className="text-xs text-gray-500">{group.structureTypeLabel}</p>
                            </div>
                            {group.assets.map((asset) => {
                                const saleAmount = radFunding[asset.assetId] || 0;
                                return (
                                    <div key={asset.assetId} className="space-y-2">
                                        <RadFundingAssetSlider
                                            asset={asset}
                                            saleAmount={saleAmount}
                                            onFundingChange={handleFundingChange}
                                            reserved={reservedByAsset[asset.assetId] || 0}
                                        />
                                        {asset.typeId === 'shares' && saleAmount > 0 && (
                                            <RadFundingShareCgtPanel
                                                factFind={factFind}
                                                asset={asset}
                                                saleAmount={saleAmount}
                                                entityId={group.entityId}
                                                cgtOverrides={cgtOverrides}
                                                onCgtOverrideChange={handleCgtOverrideChange}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}

                    {entityRadGrossAllocated > 0 && (
                        <div className="mt-4 p-3 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg text-sm text-slate-700">
                            {ENTITY_STRUCTURE_FUNDING_DISCLAIMER}
                        </div>
                    )}

                    {personalLiquidAssets.length === 0 && entityLiquidGroups.length === 0 && (
                        <p className="text-sm text-gray-500 italic">
                            No liquid assets available for RAD funding in your fact-find.
                        </p>
                    )}

                    <div className="mt-6 pt-4 border-t space-y-2">
                        {hasShareLiquidation && (
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Share / fund liquidations (gross)</span>
                                <span>
                                    {formatCurrency(
                                        Object.entries(fundingTotals.byKey)
                                            .filter(([k]) => k.startsWith('shares-'))
                                            .reduce((s, [, e]) => s + e.gross, 0)
                                    )}
                                </span>
                            </div>
                        )}
                        <div className="flex justify-between font-bold">
                            <span>Total toward RAD (net of CGT on shares):</span>
                            <span className={fundingTotals.netTotal > radInput ? 'text-red-500' : ''}>
                                {formatCurrency(fundingTotals.netTotal)}
                            </span>
                        </div>
                        <div className="flex justify-between font-bold pt-2 border-t border-gray-200">
                            <span>Remaining to Fund:</span>
                            <span>{formatCurrency(remainingToFund)}</span>
                        </div>
                    </div>
                    {saleTimingSlot && <div className="mt-4">{saleTimingSlot}</div>}
                    {liquidityShortfall > 0 && (
                        <div className="mt-4 p-3 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg text-sm space-y-2">
                            <p>
                                <strong className="text-amber-800">Heads-up:</strong> This leaves only {formatCurrency(remainingCash)} in
                                personal cash. As a rule of thumb, it&apos;s wise to keep a minimum emergency
                                fund of about {formatCurrency(recommendedEmergencyFund)} (the greater of ~6 months of living
                                expenses or {formatCurrency(50000)}){futureCapitalExpenses > 0 ? <>, plus your known future expenses ({formatCurrency(futureCapitalExpenses)})</> : ''} — and this strategy doesn&apos;t.
                                It&apos;s your call: you can proceed if you&apos;re comfortable with a smaller buffer.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RadFundingStrategy;
